type ValidationValue = boolean | number | string | RegExp;
type ValidationValueMessage<T extends ValidationValue = ValidationValue> = {
  value: T;
  message: string;
};
type ValidationRule<T extends ValidationValue = ValidationValue> = T | ValidationValueMessage<T>;

type ValidationMap<T extends Record<string, ValidationValue>> = {
  [Property in keyof T]?: T[Property] | ValidationRule<T[Property]>;
};

export type Validator = (value: any) => string | undefined;

export type ValidationRules = ValidationMap<{
  required: boolean;
  maxLength: number;
  minLength: number;
  max: number;
  min: number;
  email: boolean;
  url: boolean;
  pattern: RegExp;
}>;

export type Validation = ValidationRules & {
  validate?: (value: any) => string | undefined;
};

const isNullOrUndefined = (value: unknown): value is null | undefined => value == null;
const isRegex = (value: any): value is RegExp => value instanceof RegExp;
const isString = (value: any): value is string => typeof value === 'string';
const isObject = <T extends Record<string, unknown>>(value: unknown): value is T =>
  !isNullOrUndefined(value) &&
  !Array.isArray(value) &&
  typeof value === 'object' &&
  !(value instanceof Date);

const getValueAndMessage = (validationRule: ValidationRule) =>
  isObject(validationRule) && !isRegex(validationRule)
    ? validationRule
    : { value: validationRule, message: '' };

export const Validators: Record<
  keyof Omit<Validation, 'validate'>,
  (value: any, rule: ValidationValue) => string | undefined
> = {
  required: (value, rule) => (!!rule && !value ? 'This field is required.' : undefined),
  maxLength: (value, rule) =>
    isString(value) && value.length > parseInt(`${rule}`)
      ? `Maximum length is ${rule} characters.`
      : undefined,
  minLength: (value, rule) =>
    isString(value) && value.length < parseInt(`${rule}`)
      ? `Minimum length is ${rule} characters.`
      : undefined,
  max: (value, rule) =>
    !isNullOrUndefined(value) && parseFloat(value) > parseInt(`${rule}`)
      ? `Maximum value is ${rule}.`
      : undefined,
  min: (value, rule) =>
    !isNullOrUndefined(value) && parseFloat(value) < parseInt(`${rule}`)
      ? `Minimum value is ${rule}.`
      : undefined,
  email: (value, rule) =>
    !!rule &&
    isString(value) &&
    !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i.test(
      value,
    )
      ? 'Invalid email address.'
      : undefined,
  url: (value, rule) =>
    !!rule && isString(value) && !/^(?:\w+:)?\/\/([^\s.]+\.\S{2}|localhost[:?\d]*)\S*$/i.test(value)
      ? 'Invalid URL.'
      : undefined,
  pattern: (value, rule) =>
    isString(value) && isRegex(rule) && !value.match(rule) ? 'This field is invalid.' : undefined,
};

export const createValidate =
  (validation: Validation, validationErrorMessage?: string): Validator =>
  (value: any) => {
    const { validate, ...validationRules } = validation;

    const error = Object.entries(validationRules).reduce(
      (validationError, [rule, validationRule]) => {
        if (validationError || !validationRule) return validationError;

        const output = getValueAndMessage(validationRule);
        const validator = Validators[rule as keyof Omit<Validation, 'validate'>];
        const result = validator?.(value, output.value);

        return result ? output.message || validationErrorMessage || result : undefined;
      },
      undefined as string | undefined,
    );

    return error || validate?.(value) || undefined;
  };
